import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

export interface CompatibilityResult {
  id?: string;
  user_id: string;
  target_user_id: string;
  compatibility_score: number;
  ai_summary: string | null;
  common_points: string[] | null;
  differences: string[] | null;
  ai_model?: string;
  created_at?: string;
}

export function useProfileCompatibility(targetUserId: string | undefined) {
  const [result, setResult] = useState<CompatibilityResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const { user } = useAuth();

  // Fetch existing compatibility from database
  useEffect(() => {
    async function fetchExisting() {
      if (!user?.id || !targetUserId) {
        setIsFetching(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profile_compatibility')
          .select('*')
          .eq('user_id', user.id)
          .eq('target_user_id', targetUserId)
          .maybeSingle();

        if (error) {
          debugLogger.error('ProfileCompatibility', 'Error fetching existing:', error);
        } else if (data) {
          debugLogger.debug('ProfileCompatibility', 'Found cached compatibility:', data.compatibility_score);
          setResult(data as CompatibilityResult);
        }
      } catch (err) {
        debugLogger.error('ProfileCompatibility', 'Fetch error:', err);
      } finally {
        setIsFetching(false);
      }
    }

    fetchExisting();
  }, [user?.id, targetUserId]);

  const calculateCompatibility = useCallback(async () => {
    if (!user?.id || !targetUserId) {
      toast.error('Erro ao calcular compatibilidade');
      return;
    }

    if (user.id === targetUserId) {
      toast.error('Não é possível calcular compatibilidade consigo mesmo');
      return;
    }

    setIsLoading(true);
    debugLogger.debug('ProfileCompatibility', 'Calculating compatibility...', { targetUserId });

    try {
      const { data, error } = await supabase.functions.invoke('calculate-compatibility', {
        body: { currentUserId: user.id, targetUserId }
      });

      if (error) {
        debugLogger.error('ProfileCompatibility', 'Edge function error:', error);
        
        if (error.message?.includes('429')) {
          toast.error('Limite de requisições excedido. Tente novamente em alguns minutos.');
        } else if (error.message?.includes('402')) {
          toast.error('Créditos insuficientes para análise de IA.');
        } else {
          toast.error('Erro ao calcular compatibilidade. Tente novamente.');
        }
        return;
      }

      if (data?.error) {
        debugLogger.error('ProfileCompatibility', 'API error:', data.error);
        toast.error(data.error);
        return;
      }

      debugLogger.debug('ProfileCompatibility', 'Compatibility calculated:', data.compatibility_score);
      setResult(data as CompatibilityResult);
      toast.success('Compatibilidade calculada com sucesso!');

    } catch (err) {
      debugLogger.error('ProfileCompatibility', 'Calculate error:', err);
      toast.error('Erro inesperado ao calcular compatibilidade');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, targetUserId]);

  const recalculateCompatibility = useCallback(async () => {
    if (!user?.id || !targetUserId) {
      toast.error('Erro ao recalcular compatibilidade');
      return;
    }

    setIsLoading(true);
    debugLogger.debug('ProfileCompatibility', 'Recalculating - clearing cache first...', { targetUserId });

    try {
      // 1. Delete cache before calling API
      const { error: deleteError } = await supabase
        .from('profile_compatibility')
        .delete()
        .eq('user_id', user.id)
        .eq('target_user_id', targetUserId);

      if (deleteError) {
        debugLogger.error('ProfileCompatibility', 'Error clearing cache:', deleteError);
      } else {
        debugLogger.debug('ProfileCompatibility', 'Cache cleared successfully');
      }

      // 2. Call Edge Function
      const { data, error } = await supabase.functions.invoke('calculate-compatibility', {
        body: { currentUserId: user.id, targetUserId }
      });

      if (error) {
        debugLogger.error('ProfileCompatibility', 'Edge function error:', error);
        
        if (error.message?.includes('429')) {
          toast.error('Limite de requisições excedido. Tente novamente em alguns minutos.');
        } else if (error.message?.includes('402')) {
          toast.error('Créditos insuficientes para análise de IA.');
        } else {
          toast.error('Erro ao recalcular compatibilidade. Tente novamente.');
        }
        return;
      }

      if (data?.error) {
        debugLogger.error('ProfileCompatibility', 'API error:', data.error);
        toast.error(data.error);
        return;
      }

      debugLogger.debug('ProfileCompatibility', 'Compatibility recalculated:', data.compatibility_score);
      setResult(data as CompatibilityResult);
      toast.success('Compatibilidade recalculada com sucesso!');

    } catch (err) {
      debugLogger.error('ProfileCompatibility', 'Recalculate error:', err);
      toast.error('Erro inesperado ao recalcular compatibilidade');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, targetUserId]);

  return {
    result,
    isLoading,
    isFetching,
    calculateCompatibility,
    recalculateCompatibility,
    hasExisting: !!result
  };
}
